import { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/config';
import { Button } from '@/components/ui/button';
import { Leaf, LogOut, User, Package, MessageSquare, Eye, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import VendorBoxList from '@/components/VendorBoxList';
import VendorProfile from '@/components/VendorProfile';
import VendorAnalytics from '@/components/VendorAnalytics';
import BoxForm from '@/components/BoxForm';
import TicketList from '@/components/TicketList';
import TicketDetail from '@/components/TicketDetail';

const VendorDashboard = () => {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    toast.success('Logged out successfully');
    navigate('/vendor/login');
  };

  const isActive = (path) => {
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50">
      {/* Header */}
      <header className="bg-white shadow-lg border-b-2 border-green-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3 w-full md:w-auto">
              <div className="bg-gradient-to-br from-green-400 to-green-600 p-3 rounded-2xl shadow-lg">
                <Leaf className="w-8 h-8 text-white" data-testid="vendor-logo" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-800" data-testid="vendor-dashboard-title">Vendor Portal</h1>
                <p className="text-sm text-gray-600">{user?.email}</p>
              </div>
            </div>
            <div className="flex flex-wrap justify-center gap-2 w-full md:w-auto">
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="border-green-500 text-green-700 hover:bg-green-50 flex-1 md:flex-none"
                data-testid="view-public-map-btn"
              >
                <Eye className="w-4 h-4 mr-2" />
                Public Map
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-50 flex-1 md:flex-none"
                data-testid="vendor-logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Sidebar */}
          <aside className="w-full md:w-64 flex-shrink-0">
            <nav className="bg-white rounded-xl shadow-lg p-4 space-y-2">
              <Link
                to="/vendor/dashboard"
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  location.pathname === '/vendor/dashboard'
                    ? 'bg-green-100 text-green-700 font-semibold'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                data-testid="nav-my-boxes"
              >
                <Package className="w-5 h-5" />
                My Boxes
              </Link>
              
              <Link
                to="/vendor/dashboard/analytics"
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  isActive('/vendor/dashboard/analytics')
                    ? 'bg-green-100 text-green-700 font-semibold'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                data-testid="nav-analytics"
              >
                <BarChart3 className="w-5 h-5" />
                Analytics
              </Link>
              
              <Link
                to="/vendor/dashboard/profile"
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  isActive('/vendor/dashboard/profile')
                    ? 'bg-green-100 text-green-700 font-semibold'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                data-testid="nav-profile"
              >
                <User className="w-5 h-5" />
                Profile
              </Link>
              
              <Link
                to="/vendor/dashboard/support"
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  isActive('/vendor/dashboard/support')
                    ? 'bg-green-100 text-green-700 font-semibold'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                data-testid="nav-support"
              >
                <MessageSquare className="w-5 h-5" />
                Support
              </Link>
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            <Routes>
              <Route path="/" element={<VendorBoxList />} />
              <Route path="/analytics" element={<VendorAnalytics />} />
              <Route path="/profile" element={<VendorProfile />} />
              <Route path="/boxes/new" element={<BoxForm mode="create" />} />
              <Route path="/boxes/:id/edit" element={<BoxForm mode="edit" />} />
              <Route path="/support" element={<TicketList />} />
              <Route path="/support/:ticketId" element={<TicketDetail />} />
            </Routes>
          </main>
        </div>
      </div>
    </div>
  );
};

export default VendorDashboard;